'use client';

import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Stethoscope, Wrench, CheckCircle } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";
import { convertToLocaleNumber } from "@/lib/i18n/numbers";

export default function TreatmentTimelineSection() {
    const { t, locale } = useTranslation("all-on-4");
    
    const steps = [
        {
            icon: Stethoscope,
            title: t("timeline.steps.consultation.title"),
            description: t("timeline.steps.consultation.description"),
            duration: t("timeline.steps.consultation.duration")
        },
        {
            icon: Wrench,
            title: t("timeline.steps.implantPlacement.title"),
            description: t("timeline.steps.implantPlacement.description"),
            duration: t("timeline.steps.implantPlacement.duration")
        },
        {
            icon: Calendar,
            title: t("timeline.steps.temporaryTeeth.title"),
            description: t("timeline.steps.temporaryTeeth.description"),
            duration: t("timeline.steps.temporaryTeeth.duration")
        },
        {
            icon: CheckCircle,
            title: t("timeline.steps.finalProsthetics.title"),
            description: t("timeline.steps.finalProsthetics.description"),
            duration: t("timeline.steps.finalProsthetics.duration")
        }
    ];

    return (
        <section
            id="timeline"
            className="py-16 lg:py-24 bg-white dark:bg-slate-800"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t("timeline.title").includes("All-On-4") ? (
                            <>
                                {t("timeline.title").split("All-On-4")[0]}
                                <span className="text-gold">All-On-4</span>
                                {t("timeline.title").split("All-On-4")[1]}
                            </>
                        ) : t("timeline.title")}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("timeline.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {steps.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                                <CardContent className="p-6 text-center">
                                    <div className="relative mb-6">
                                        <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto">
                                            <step.icon className="w-8 h-8 text-gold" />
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gold text-navy rounded-full flex items-center justify-center text-sm font-bold">
                                            {convertToLocaleNumber(index + 1, locale)}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <span className="text-xs text-gold font-medium uppercase tracking-wide">
                                            {step.duration}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-semibold text-navy dark:text-white mb-3">
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300 text-sm">
                                        {step.description}
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
} 